import cn from "@/util/cn"
import { DateTimeFormatter, LocalDate, ZoneId } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons"
import { useState } from "react"

type MonthCalendarProps = {
  selectedWeekStart: LocalDate
  onPickWeekContaining: (day: LocalDate) => void
}

const MONTH_LABEL = DateTimeFormatter.ofPattern("MMMM yyyy").withLocale(Locale.US)
const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"]

const startOfWeekSunday = (date: LocalDate): LocalDate => {
  // js-joda DayOfWeek: MON=1..SUN=7. Sunday-start week boundary.
  const daysFromSunday = date.dayOfWeek().value() % 7
  return date.minusDays(daysFromSunday)
}

const buildGrid = (visibleMonth: LocalDate): LocalDate[] => {
  const firstOfMonth = visibleMonth.withDayOfMonth(1)
  const gridStart = startOfWeekSunday(firstOfMonth)
  return Array.from({ length: 42 }, (_, i) => gridStart.plusDays(i))
}

const MonthCalendar = ({ selectedWeekStart, onPickWeekContaining }: MonthCalendarProps) => {
  const [visibleMonth, setVisibleMonth] = useState<LocalDate>(selectedWeekStart)
  const today = LocalDate.now(ZoneId.systemDefault())
  const days = buildGrid(visibleMonth)
  const selectedWeekEnd = selectedWeekStart.plusDays(6)

  const goPrevMonth = () => setVisibleMonth((current) => current.minusMonths(1).withDayOfMonth(1))
  const goNextMonth = () => setVisibleMonth((current) => current.plusMonths(1).withDayOfMonth(1))

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-primary-contrast">
          {visibleMonth.format(MONTH_LABEL)}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={goPrevMonth}
            aria-label="Previous month"
            className="rounded p-1 text-primary-contrast/70 hover:bg-secondary"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={goNextMonth}
            aria-label="Next month"
            className="rounded p-1 text-primary-contrast/70 hover:bg-secondary"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-center text-xs text-primary-contrast/60">
        {DAY_LABELS.map((label, i) => (
          <span key={i} className="py-1">
            {label}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-y-2">
        {days.map((day) => {
          const isInMonth = day.month() === visibleMonth.month()
          const isFuture = day.isAfter(today)
          const isFirstOfWeek = day.equals(selectedWeekStart)
          const isLastOfWeek = day.equals(selectedWeekEnd)
          const inSelectedWeek = !day.isBefore(selectedWeekStart) && !day.isAfter(selectedWeekEnd)
          return (
            <button
              key={day.toString()}
              type="button"
              disabled={isFuture}
              onClick={() => onPickWeekContaining(day)}
              className={cn(
                "flex h-8 items-center justify-center text-sm",
                inSelectedWeek && "bg-primary text-white",
                isFirstOfWeek && "rounded-l-full",
                isLastOfWeek && "rounded-r-full",
                !inSelectedWeek && isInMonth && "text-primary-contrast",
                !inSelectedWeek && !isInMonth && "text-primary-contrast/40",
                isFuture && "cursor-not-allowed text-primary-contrast/20",
              )}
            >
              {String(day.dayOfMonth()).padStart(2, "0")}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default MonthCalendar
